import type { Order } from "@/type/order";
import type { OrderDetails } from "@/type/order/orderDetails";
import { HttpClient } from "@/services/httpClient";
import { Controllers, FrappeMethodType, HttpClientMethods } from "@/type/api";
import type { CustomOrderStatus } from "@/type/order";
import {
  buildFrappeFilters,
  buildFrappeLikeSearchFilters,
  serializeFrappeFilters,
  type FrappeFilterClause,
} from "@/services/frappe/filters";
import { envelopeErrorMessage, unwrapStandardEnvelope } from "@/services/frappe/envelope";

/**
 * Whitelisted order methods. The status write goes through here and NOWHERE else — see
 * `updateOrderStatus` for why a doctype PUT is not an acceptable substitute.
 */
const ORDER_METHODS = {
  transitionOrder: "pet_app.api.order.transition_order",
} as const;

export interface FetchOrdersParams {
  search?: string;
  page?: number;
  itemsPerPage?: number;
  sortBy?: string;
  orderBy?: string;
  filters?: FrappeFilterClause[];
  orderStatus?: string | null;
  paymentStatus?: string | null;
  customerId?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
}

async function fetchSalesOrderCount(filters: any[]): Promise<number> {
  const orderService = new HttpClient(Controllers.order);
  const resp = await orderService.request<any>(
    {
      url: "frappe.client.get_count",
      method: HttpClientMethods.GET,
      params: {
        doctype: "Sales Order",
        filters: JSON.stringify(filters),
      },
    },
    {},
    FrappeMethodType.method,
  );

  const count =
    typeof resp?.message === "number"
      ? resp.message
      : typeof resp?.data === "number"
        ? resp.data
        : typeof resp?.count === "number"
          ? resp.count
          : 0;

  return Number.isFinite(count) ? count : 0;
}

export async function fetchOrders({
  search = "",
  page = 1,
  itemsPerPage = 10,
  sortBy = "transaction_date",
  orderBy = "desc",
  orderStatus = null,
  paymentStatus = null,
  customerId = null,
  dateFrom = null,
  dateTo = null,
  filters: customFilters,
}: FetchOrdersParams = {}): Promise<{ data: Order[]; total: number }> {
  const fields = [
    "name",
    "transaction_date",
    "customer",
    "customer_name",
    "customer.image",
    "grand_total",
    "custom_order_status",
    "custom_payment_status",
    "custom_payment_method",
  ];

  const filters = customFilters ?? [
    ...buildFrappeLikeSearchFilters(["customer_name"], search),
    ...buildFrappeFilters([
      orderStatus
        ? { field: "custom_order_status", operator: "=", value: orderStatus }
        : null,
      paymentStatus
        ? { field: "custom_payment_status", operator: "=", value: paymentStatus }
        : null,
      customerId ? { field: "customer", operator: "=", value: customerId } : null,
      dateFrom ? { field: "transaction_date", operator: ">=", value: dateFrom } : null,
      // ⚠️ NO `is_stock` CLAUSE. Sales Order has no such field, so the moment the removed
      // Stock control was touched Frappe rejected the whole list query — the filter could
      // only ever break the request, never narrow it.
      dateTo ? { field: "transaction_date", operator: "<=", value: dateTo } : null,
    ]),
  ];

  const params = {
    fields: JSON.stringify(fields),
    limit_page_length: itemsPerPage,
    limit_start: (page - 1) * itemsPerPage,
    order_by: `${sortBy} ${orderBy}`,
    filters: serializeFrappeFilters(filters),
  };

  // Use empty string as controller for direct resource path
  const orderService = new HttpClient(Controllers.order);
  // Expect frappe-style response: { data: Order[], total: number }
  const response = await orderService.get<{ data: Order[]; total?: number }>(
    undefined,
    {},
    params,
    JSON.stringify(fields),
  );

  const total =
    typeof response.total === "number"
      ? response.total
      : await fetchSalesOrderCount(filters).catch(() => {
          // fallback if count method isn't available
          return Array.isArray(response.data) ? response.data.length : 0;
        });

  return {
    data: response.data || [],
    total,
  };
}

/**
 * Fetch order details by order ID
 * @param id Order name/id (e.g., 'SAL-ORD-2026-00010')
 * @returns OrderDetails object
 */
export async function fetchOrderDetailsById(id: string): Promise<OrderDetails> {
  const orderService = new HttpClient(Controllers.order);
  // GET /api/resource/Sales%20Order/{id}
  const response = await orderService.get<{ data: OrderDetails }>(
    `/${encodeURIComponent(id)}`,
  );
  return response.data;
}

/**
 * The `data` payload of a successful `transition_order`.
 *
 * ⚠️ REJECTION ARRIVES AS HTTP 200 WITH `ok: false`, so `ok` is the only thing that may
 * decide success — never the HTTP status, never "no exception was thrown". Locating the
 * envelope is `unwrapStandardEnvelope`'s job; see services/frappe/envelope.ts for why it
 * has to look in two places.
 */
interface OrderTransitionData {
  order?: string;
  previous_status?: string;
  new_status?: string;
}

export interface OrderTransitionResult {
  order: string;
  previous_status: string | null;
  new_status: string;
}

/** Exactly the two parameters `transition_order(order, target_status)` declares. */
interface OrderTransitionArgs {
  order: string;
  target_status: string;
}

/**
 * Move an order to a new status — THROUGH THE VALIDATED ENDPOINT.
 *
 * ⚠️ THIS REPLACES A RAW `PUT` ON `Sales Order.custom_order_status`. That write bypassed
 * transition validation, permission gating and rollback protection completely, and with
 * `allow_on_submit` now enabled on the field it would have been accepted — letting an
 * operator move an order Draft → Completed with no stock movement and no accounting.
 * There is no doctype write path left in this module; do not reintroduce one.
 *
 * The failure MESSAGE IS THE BACKEND'S, verbatim. It already distinguishes an illegal
 * transition from an unsupported target from a missing permission, and a client-side
 * copy of those rules would drift the moment the backend's change.
 */
export async function updateOrderStatus(
  id: string,
  status: CustomOrderStatus | string,
): Promise<OrderTransitionResult> {
  const order = String(id ?? "").trim();
  const targetStatus = String(status ?? "").trim();

  if (!order || !targetStatus)
    throw new Error("updateOrderStatus requires an order and a target status");

  // Empty controller → dispatched as a whitelisted METHOD, never as a doctype resource.
  const orderService = new HttpClient("" as Controllers);

  /**
   * ⚠️ THE ARGUMENTS GO IN THE BODY — `post(body, url, params, …)`, first slot, NOT third.
   *
   * They used to sit in `params`, i.e. the QUERY STRING, with `{}` as the body. That is
   * what produced `transition_order() missing 2 required positional arguments`: the
   * request arrived, the method ran, and `form_dict` was empty. Frappe builds `form_dict`
   * from the JSON body ALONE whenever the request has a JSON content type and a non-empty
   * body — and axios sends `Content-Type: application/json` on every request
   * (plugins/axios.ts:9), so `{}` was a non-empty body that DISCARDED the query string
   * rather than merging with it. Query args are read only when there is no JSON body.
   *
   * Patterned on `productStatusAction` (productApi.ts:582-590), which posts
   * `{ product_id }` as the body with `{}` as params and works today — the same shape
   * `UpdateAccountingSettings` (accountingSettingsApi.ts:61-73) and `UpdateProduct` use.
   * Top-level kwargs, no nesting: `transition_order` takes two positional parameters, not
   * a `data` dict.
   */
  const response = await orderService.post<any, OrderTransitionArgs>(
    { order, target_status: targetStatus },
    ORDER_METHODS.transitionOrder,
    {},
    {},
    FrappeMethodType.method,
  );

  /**
   * ⚠️ SAME BUG AS `list_coupons` HAD, and it was here too: this used to assert
   * `payload.ok === true` against `response.message`, but the axios interceptor
   * (httpClient.ts:284-291) REPLACES `message` with the envelope's `data` whenever
   * `ok === true`. So the check read the collapsed data, found no `ok`, and threw on
   * every SUCCESSFUL transition. `unwrapStandardEnvelope` handles both shapes.
   *
   * The `ok` discipline is unchanged — absent or malformed is still a failure.
   */
  const envelope = unwrapStandardEnvelope<OrderTransitionData>(response);

  if (!envelope || !envelope.ok) {
    const reason = envelopeErrorMessage(envelope);

    throw new Error(reason || `${ORDER_METHODS.transitionOrder} rejected ${order}`);
  }

  const data = envelope.data ?? {};

  return {
    order: String(data.order ?? order),
    previous_status: data.previous_status ?? null,
    // Fall back to what was requested only for display; the server has already agreed.
    new_status: String(data.new_status ?? targetStatus),
  };
}
